import { useEffect, useState } from 'react'
import { HostOnboardingSteps, OnboardingWizardPages } from '../utils/constants'
import { notifications } from '@mantine/notifications'
import {
  OnboardingState,
  defaultOnboardingState,
} from '../models/Onboarding/OnboardingState'
import { OnboardingLocation } from '../models/Onboarding/OnboardingLocation'
import { OnboardingDevice } from '../models/Onboarding/OnboardingDevice'
import { useSessionInfo } from '../contexts/SessionContext'
import { OnboardingWizard } from '../components/onboarding/OnboardingWizard'
import {
  fetchOnboardingState,
  initializeOnboardingState,
} from '../data/organization.service'
import { getCurrentStepFromOnboardingState } from '../utils/onboardingUtils'
import { useMsal } from '@azure/msal-react'

function HostOnboarding() {
  const { session } = useSessionInfo()
  const { instance } = useMsal()
  const organizationId = session?.organizationId
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(
    defaultOnboardingState,
  )
  const [isLoadingOnboardingState, setIsLoadingOnboardingState] =
    useState(false)
  const [currentStep, setCurrentStep] = useState<HostOnboardingSteps>(
    HostOnboardingSteps.Instructions,
  )

  useEffect(() => {
    const getOnboardingState = async (organizationId: number | undefined) => {
      if (organizationId == null) return

      setIsLoadingOnboardingState(true)
      try {
        const onboardingState = await fetchOnboardingState(
          organizationId,
          instance,
        )
        setOnboardingState(onboardingState)
        const currentStep = getCurrentStepFromOnboardingState(onboardingState)
        setCurrentStep(currentStep)
      } catch (ex: any) {
        if (ex?.response?.status === 404) {
          await initializeOnboarding(organizationId)
        } else {
          console.log(ex)
        }
      } finally {
        setIsLoadingOnboardingState(false)
      }
    }

    const initializeOnboarding = async (organizationId: number) => {
      try {
        const onboardingState = await initializeOnboardingState(
          organizationId,
          instance,
        )
        setOnboardingState(onboardingState)
      } catch (ex: any) {
        notifications.show({
          title: 'An unknown error occurred',
          message:
            'Unable to get organization info. Refresh page. If you are unable to resolve the issue, please contact us for help',
          autoClose: false,
        })
      }
    }

    getOnboardingState(organizationId)
  }, [organizationId, instance])

  const handleStripeConnectCompleted = () => {
    setOnboardingState((prev) => ({
      ...prev,
      isStripeOnboardingComplete: true,
    }))
  }

  const handleLocationUpdate = (location: OnboardingLocation) => {
    setOnboardingState((prev) => ({
      ...prev,
      location: location,
    }))
  }

  const handleDeviceUpdate = (device: OnboardingDevice | null) => {
    setOnboardingState((prev) => ({
      ...prev,
      device: device ?? undefined,
    }))
  }

  return (
    <OnboardingWizard
      location={onboardingState.location}
      setLocation={handleLocationUpdate}
      device={onboardingState.device ?? null}
      setDevice={handleDeviceUpdate}
      isLoadingState={isLoadingOnboardingState}
      pageName={OnboardingWizardPages.Onboarding}
      currentStep={currentStep}
      handleStripeConnectCompleted={handleStripeConnectCompleted}
      setCurrentStep={setCurrentStep}
      stripeConnectId={onboardingState.stripeConnectId}
      isStripeOnboardingComplete={onboardingState.isStripeOnboardingComplete}
    />
  )
}

export default HostOnboarding
